import { PerformanceExpectationGroup } from '../../activity-outcomes.constants';

export type ComputeNextGroupsAfterSaveParams = {
  groups: PerformanceExpectationGroup[];
  formState: PerformanceExpectationGroup;
  isAddNew: boolean;
  editingIndex: number | null;
  maxPeBlocks?: number;
};

/**
 * Computes the next PE groups array after saving from add or edit form.
 *
 * Determines the new groups array based on whether the user was adding a new
 * Performance Expectation block or editing an existing one, and whether the
 * section is capped to a single block (maxPeBlocks === 1). Three cases:
 * - Add new + maxPeBlocks === 1: replace all groups with the single saved formState.
 * - Add new + maxPeBlocks !== 1: append formState to existing groups.
 * - Edit existing: replace the group at editingIndex with formState.
 *
 * @param params - Named parameters: groups, formState, isAddNew, editingIndex, maxPeBlocks.
 * @returns The new groups array to set and persist.
 *
 * @example
 * ```typescript
 * const next = computeNextGroupsAfterSave({
 *   groups: [existing],
 *   formState: newBlock,
 *   isAddNew: true,
 *   editingIndex: null,
 *   maxPeBlocks: 1,
 * });
 * expect(next).toEqual([newBlock]);
 * ```
 */
function computeNextGroupsAfterSave({
  groups,
  formState,
  isAddNew,
  editingIndex,
  maxPeBlocks,
}: ComputeNextGroupsAfterSaveParams): PerformanceExpectationGroup[] {
  if (isAddNew && maxPeBlocks === 1) {
    return [formState];
  }
  if (isAddNew) {
    return [...groups, formState];
  }
  const index = editingIndex ?? 0;
  return [...groups.slice(0, index), formState, ...groups.slice(index + 1)];
}

export default computeNextGroupsAfterSave;
